import { DestroyRef, Injectable, inject } from '@angular/core';

/**
 * Há quanto tempo o usuário precisa ter saído para a volta contar como chegada.
 *
 * Abaixo disso a abertura é um refresh, uma aba nova, um voltar do navegador: a
 * tela de abertura ainda aparece, mas só pelo tempo que a carga levar. Acima,
 * ela cumpre o tempo mínimo inteiro (ver `SplashScreenComponent`).
 */
const AWAY_THRESHOLD_MS = 60 * 60 * 1000;

/** De quanto em quanto tempo a marca é renovada com a aba aberta. */
const HEARTBEAT_MS = 60 * 1000;

const STORAGE_KEY = 'rapidocs:last-seen-at';

/**
 * Quando o usuário esteve por aqui pela última vez.
 *
 * A marca é renovada enquanto a aba está visível e no momento em que ela some
 * (`pagehide`/`visibilitychange`). Renovar em batida, e não só na saída, é o que
 * salva o caso em que o navegador é morto sem avisar: a marca fica no máximo um
 * minuto atrasada, em vez de ficar na última visita.
 */
@Injectable({ providedIn: 'root' })
export class AppSessionService {
    private _destroyRef = inject(DestroyRef);

    /**
     * Lido no construtor, ANTES de qualquer renovação: depois que o heartbeat
     * começa a escrever, a marca antiga já não existe para ser comparada.
     */
    private readonly _awayForLong = this._readAwayForLong();

    private _timer: ReturnType<typeof setInterval> | null = null;

    /** A volta foi depois de um tempo longo fora (ou é o primeiro acesso). */
    public isReturningAfterLongAway(): boolean {
        return this._awayForLong;
    }

    /** Liga a renovação da marca. Chamado uma vez, no bootstrap. */
    public trackPresence(): void {
        if (this._timer) return;

        this._mark();
        this._timer = setInterval(() => this._mark(), HEARTBEAT_MS);

        // `pagehide` cobre o fechar da aba e o bfcache; `visibilitychange` cobre
        // trocar de aba e minimizar, que no celular é como o app "sai".
        const onHide = () => this._mark();
        window.addEventListener('pagehide', onHide);
        document.addEventListener('visibilitychange', onHide);

        this._destroyRef.onDestroy(() => {
            if (this._timer) clearInterval(this._timer);
            window.removeEventListener('pagehide', onHide);
            document.removeEventListener('visibilitychange', onHide);
        });
    }

    private _readAwayForLong(): boolean {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return true;

            const lastSeen = Number(raw);
            if (!Number.isFinite(lastSeen)) return true;

            return Date.now() - lastSeen >= AWAY_THRESHOLD_MS;
        } catch {
            // Modo anônimo com storage bloqueado: cada abertura é uma chegada.
            return true;
        }
    }

    private _mark(): void {
        try {
            localStorage.setItem(STORAGE_KEY, String(Date.now()));
        } catch {
            // Sem storage não há marca, e toda volta conta como chegada.
        }
    }
}
